/**
 * Utility functions for handling HTML content
 */

/**
 * Strips HTML tags from a string and returns plain text
 * @param {string} htmlString - The HTML string to strip tags from
 * @returns {string} - Plain text without HTML tags
 */
export const stripHtmlTags = (htmlString) => {
	if (!htmlString || typeof htmlString !== 'string') {
		return '';
	}
	return htmlString.replace(/<[^>]*>/g, '').trim();
};

/**
 * Checks if an HTML string contains actual text content (not just empty tags)
 * @param {string} htmlString - The HTML string to check
 * @returns {boolean} - True if the string contains meaningful text content
 */
export const hasTextContent = (htmlString) => {
	const textContent = stripHtmlTags(htmlString);
	return textContent.length > 0;
};

/**
 * Truncates HTML content to a specified length, preserving text content
 * @param {string} htmlString - The HTML string to truncate
 * @param {number} maxLength - Maximum length of the text content
 * @param {string} suffix - Suffix to add if truncated (default: '...')
 * @returns {string} - Truncated text content
 */
export const truncateHtmlContent = (
	htmlString,
	maxLength = 100,
	suffix = '...'
) => {
	const textContent = stripHtmlTags(htmlString);
	if (textContent.length <= maxLength) {
		return textContent;
	}
	return textContent.slice(0, maxLength) + suffix;
};

/**
 * Safely renders content that could be either plain text or HTML
 * If the content contains HTML tags, it renders as HTML, otherwise as plain text
 * @param {string} content - The content to render (could be plain text or HTML)
 * @returns {object} - Object with dangerouslySetInnerHTML or children for safe rendering
 */
export const safeRenderContent = (content) => {
	if (!content || typeof content !== 'string') {
		return { children: '' };
	}

	// Check if content contains HTML tags
	const hasHtmlTags = /<[^>]*>/g.test(content);

	if (hasHtmlTags) {
		// Content contains HTML tags, render as HTML
		return {
			dangerouslySetInnerHTML: {
				__html: content,
			},
		};
	} else {
		// Content is plain text, render as text
		return {
			children: content,
		};
	}
};
